import uuidv4 from 'uuid/v4';
import { ReactNode } from 'react';
import { GridSize } from '@material-ui/core';
import { forOwn, groupBy, omit } from 'lodash';

import {
  Client,
  Network,
  ProfileSuggestion,
  PublicUserProfile,
  ReachStat,
  S3Object,
  S3ObjectInput,
  SearchAggregates,
  SearchResultsObject,
  SocialAccount,
  SuggestionResultsObject,
  UserProfile,
  UserType,
} from 'api';
import {
  IClientSettingParams,
  IFilter,
  IVideoInput,
  SearchResultTypes,
  SuggestionTypes,
} from 'types/app';
import { AuthState } from 'types/aws-amplify';
import * as paths from 'paths';
import { isRoot, popup, queryString } from 'utilities';
import { NO_NAME } from 'constants/index';
import numeral from 'numeral';

const USER_IMAGES_DIRECTORY = 'user_images';
const USER_CSV_DIRECTORY = 'user_csv';
const APP_IMAGES_DIRECTORY = 'webapp';
const {
  NODE_ENV,
  PORT,
  REACT_APP_DOMAIN,
  AUTH_DOMAIN,
  LEGACY_AUTH_DOMAIN,
  REACT_APP_IMGIX_HOST,
  REACT_APP_IMAGES_BUCKET,
  REACT_APP_VIDEOS_BUCKET,
  REACT_APP_CSV_BUCKET,
  REACT_APP_REGION,
} = process.env;

export interface IReachByProvider {
  provider: string;
  reach: number;
}

export const getFullName = (user: Partial<PublicUserProfile & ProfileSuggestion>) => {
  if (!user) {
    return NO_NAME;
  }
  return user.full_name || user.email_address || user.title || NO_NAME;
};

export const isSignedIn = (state: AuthState) => {
  return state === AuthState.signedIn;
};

export const isSignedOut = (state: AuthState) => {
  const signedOutStates = omit(AuthState, [
    AuthState.signedIn,
    AuthState.loading,
    AuthState.authenticating,
  ]);
  return !!signedOutStates[state];
};

export const getAuthStateFromPath = (pathname: string) => {
  let state: AuthState = AuthState.signIn;

  const params = new URLSearchParams(window.location.search);
  const isAuthenticating = params.has('code') && params.has('state');

  if (isRoot() && isAuthenticating) {
    return AuthState.authenticating;
  }

  if (isRoot()) {
    return AuthState.clientSelect;
  }

  if (pathname === paths.signUp) {
    state = AuthState.signUp;
  }

  if (pathname === paths.invitation) {
    state = AuthState.invitation;
  }

  if (pathname === paths.forgotPassword) {
    state = AuthState.forgotPassword;
  }

  return state;
};

export const getRandomProfileImage = () => {
  const NUM_PROFILE_IMAGES = 6;
  const index = Math.floor(Math.random() * NUM_PROFILE_IMAGES) + 1;
  const imageUrl = `${REACT_APP_IMGIX_HOST}/${APP_IMAGES_DIRECTORY}/profile-${index}.png?w=100&h=100auto=format`;

  return getImageSrc(imageUrl);
};

export const generateS3Image = (blob: Blob, file: File, user: UserProfile): S3ObjectInput => {
  const key = `${USER_IMAGES_DIRECTORY}/${user.id}/${uuidv4()}`;

  return {
    key,
    localUri: (blob as unknown) as string, // backend doesn't know Blob
    mimeType: file.type,
    bucket: REACT_APP_IMAGES_BUCKET,
    region: REACT_APP_REGION,
  };
};

export const generateS3CSV = (data: string, fileName: string, user: UserProfile): S3ObjectInput => {
  const extension = fileName.split('.')[1].toLowerCase();
  const key = `${USER_CSV_DIRECTORY}/${user.id}/${uuidv4()}.${extension}`;
  return {
    key,
    localUri: data,
    mimeType: 'text/csv',
    bucket: REACT_APP_CSV_BUCKET,
    region: REACT_APP_REGION,
  };
};

export function generateS3Video(file: File): IVideoInput {
  const extension = file.name.split('.').pop();
  const key = `${uuidv4()}${extension && '.'}${extension}`;

  return {
    key,
    file,
    bucket: REACT_APP_VIDEOS_BUCKET,
    region: REACT_APP_REGION,
  };
}

export const isAdminUser = (user: UserProfile | PublicUserProfile) => {
  const admin = user.user_type === UserType.Admin;
  const cs = user.user_type === UserType.Cs;

  return admin || cs;
};

export const isClientSuccessUser = (user: UserProfile | PublicUserProfile) => {
  return user.user_type === UserType.Cs;
};

export const isContributorUser = (user: UserProfile | PublicUserProfile) =>
  user.user_type === UserType.Contributor;

export const columnSplitToGridSizes = (columnSplit: string) => {
  const columns = columnSplit.split('/');

  return columns.map(column => {
    return parseInt(column, 10) as GridSize;
  });
};

export const getSocialAccountName = (account: SocialAccount) => {
  if (!account) {
    return '';
  }

  const { identifier, provider, profile_name } = account;
  return provider === Network.Twitter ? `@${identifier}` : profile_name;
};

export const getSuggestionResultType = (suggestion: SuggestionResultsObject) => {
  // @ts-ignore
  if (suggestion.__typename === 'GroupSuggestion') {
    return SuggestionTypes.Group;
  }

  // @ts-ignore
  if (suggestion.__typename === 'ProfileSuggestion') {
    return SuggestionTypes.Profile;
  }

  return null;
};

export const getSearchResultsType = (suggestion: SearchResultsObject) => {
  // @ts-ignore
  if (suggestion.__typename === 'Content') {
    return SearchResultTypes.Content;
  }

  // @ts-ignore
  if (suggestion.__typename === 'Group') {
    return SearchResultTypes.Group;
  }

  // @ts-ignore
  if (suggestion.__typename === 'PublicUserProfile') {
    return SearchResultTypes.Profile;
  }

  return null;
};

export const getTotalReach = (stats: ReachStat[]) => {
  return stats.reduce((current: number, stat) => current + stat.reach, 0);
};

export const getReachByProvider = (stats: ReachStat[]) => {
  const grouped = groupBy(stats, stat => stat.provider);
  const reaches: IReachByProvider[] = [];

  forOwn(grouped, (value, key) => {
    reaches.push({ provider: key, reach: getTotalReach(grouped[key]) });
  });

  return reaches;
};

export const getTotalPreviousReach = (stats: ReachStat[]) => {
  return stats.reduce((current: number, stat) => current + stat.previous_reach, 0);
};

export const getSelectedFilterTitle = (
  filters: IFilter[],
  searchAggregates: SearchAggregates,
  params: IClientSettingParams,
) => {
  const { departments, locations } = searchAggregates;
  const { department_id, location_id } = params;

  let title: ReactNode = null;

  filters.forEach(filter => {
    if (filter.isSelected) {
      title = filter.textNode;
    }
  });

  departments.forEach(department => {
    if (department.id === department_id) {
      title = department.title;
    }
  });

  locations.forEach(location => {
    if (location.id === location_id) {
      title = location.title;
    }
  });

  return title;
};

export const getImageSrc = (imageUrl: string) => ({
  src: imageUrl,
  srcSet: `${imageUrl} 1x, ${imageUrl}&dpr=2 2x, ${imageUrl}&dpr=3 3x`,
});

export const getAppImage = (filename: string) => {
  const url = `${REACT_APP_IMGIX_HOST}/${APP_IMAGES_DIRECTORY}/${filename}?auto=format`;
  return getImageSrc(url);
};

export const connectionPopup = (userId: string, provider: string, legacyAuthDomain: boolean) => {
  const params = {
    user_id: userId,
    success_callback_url: `${window.location.origin}${paths.connectionSuccess}`,
    failure_callback_url: `${window.location.origin}${paths.connectionFailure}`,
    source: 'web sharing',
  };

  const url = `${
    legacyAuthDomain ? LEGACY_AUTH_DOMAIN : AUTH_DOMAIN
  }/connect/${provider}?${queryString(params)}`;

  popup(url, 'Connection Popup', 650, 650);
};

export const getSubdomainUrl = (subdomain: string) => {
  if (NODE_ENV === 'development') {
    const prefix = subdomain.split('.')[0];
    subdomain = `${prefix}.${REACT_APP_DOMAIN}:${PORT}`;
  }

  return `${window.location.protocol}//${subdomain}`;
};

export const getContentUrlRegex = (client: Client) => {
  const { subdomain } = client;
  const prefix = subdomain.split('.')[0];

  if (NODE_ENV === 'stage' || NODE_ENV === 'development') {
    return new RegExp(
      `(https://${prefix}.everyonestage.app/content/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$`,
    );
  }

  if (NODE_ENV === 'production') {
    return new RegExp(
      `(https://${prefix}.everyonesocial.app/content/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$`,
    );
  }

  return null;
};

export const formatStat = (count: number) => {
  return count > 1000 ? numeral(count).format('0.0a') : numeral(count).format();
};

export const getS3DownloadURL = (data: S3Object) => {
  const { region, bucket, key } = data;
  return `https://s3.${region}.amazonaws.com/${bucket}/${key}`;
};
